import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only load English synchronously for fast initial render
import en from './locales/en.json';

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇲🇽' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tl', name: 'Tagalog', flag: '🇵🇭' },
] as const;

export type LanguageCode = typeof languages[number]['code'];

// Lazy load other locales only when needed
const loadLocale = async (lng: string): Promise<Record<string, unknown>> => {
  switch (lng) {
    case 'pt': return (await import('./locales/pt.json')).default;
    case 'es': return (await import('./locales/es.json')).default;
    case 'hi': return (await import('./locales/hi.json')).default;
    case 'de': return (await import('./locales/de.json')).default;
    case 'fr': return (await import('./locales/fr.json')).default;
    case 'tl': return (await import('./locales/tl.json')).default;
    case 'ja': return (await import('./locales/ja.json')).default;
    case 'ko': return (await import('./locales/ko.json')).default;
    case 'zh': return (await import('./locales/zh.json')).default;
    case 'ar': return (await import('./locales/ar.json')).default;
    case 'tr': return (await import('./locales/tr.json')).default;
    default: return en;
  }
};

// Start with only English loaded
const resources = {
  en: { translation: en },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'salespitchmasterai_language',
    },
  });

// Load the detected language if not English
const detectedLang = i18n.language?.split('-')[0];
if (detectedLang && detectedLang !== 'en' && languages.some(l => l.code === detectedLang)) {
  loadLocale(detectedLang).then(translations => {
    i18n.addResourceBundle(detectedLang, 'translation', translations, true, true);
  });
}

// Hook into language changes to lazy load
i18n.on('languageChanged', async (lng) => {
  const langCode = lng.split('-')[0];
  if (langCode !== 'en' && !i18n.hasResourceBundle(langCode, 'translation')) {
    const translations = await loadLocale(langCode);
    i18n.addResourceBundle(langCode, 'translation', translations, true, true);
  }
});

export default i18n;